import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { devicesService } from '../../services/devices';
import type { Device, DeviceFirmwareStatus } from '../../types/device';

/** How often the panel asks whether a board has come back on the new version. */
const POLL_INTERVAL_MS = 15_000;

interface DeviceFirmwareCardProps {
  gymSlug: string;
  device: Device;
  /** Handed the refreshed device whenever the server tells us something new. */
  onDeviceChange: (device: Device) => void;
}

/** Badge colour per state — the two that need attention stand out. */
const STATUS_VARIANT: Record<DeviceFirmwareStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  unknown: 'outline',
  unmanaged: 'outline',
  no_release: 'outline',
  up_to_date: 'secondary',
  update_available: 'default',
  updating: 'default',
  update_failed: 'destructive',
};

/**
 * The firmware a board is running, and the button that moves it to the current
 * release.
 *
 * Sending an update reboots the wall, so it is never automatic: the gym is told
 * a release exists by email and picks the moment. The confirmation step is the
 * point of the flow — a board that is off or off the network simply never hears
 * the command, and nothing on our side can tell the difference until the
 * timeout, so the check has to happen before it goes out rather than after.
 *
 * There is no acknowledgement in the protocol either: a board proves it updated
 * by reporting the new version at its next provisioning call, which is why this
 * polls while an update is in flight instead of waiting for a callback.
 */
export default function DeviceFirmwareCard({
  gymSlug,
  device,
  onDeviceChange,
}: DeviceFirmwareCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const firmware = device.firmware;
  const updating = firmware?.status === 'updating';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // While a command is in flight the only way to learn it landed is to ask
  // again: the board reports its version to the provisioning endpoint, not to
  // us. Polling stops as soon as the state is no longer `updating` — whether it
  // ended in success or in a timeout.
  useEffect(() => {
    if (!updating) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const tick = async () => {
      try {
        const refreshed = await devicesService.get(gymSlug, device.id);
        if (mountedRef.current) onDeviceChange(refreshed);
      } catch {
        // A dropped poll is not worth surfacing: the next tick asks again, and
        // the update runs on the board either way.
      }
    };

    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [updating, gymSlug, device.id, onDeviceChange]);

  const handleConfirm = useCallback(async () => {
    setSending(true);
    try {
      const updated = await devicesService.updateFirmware(gymSlug, device.id);
      onDeviceChange(updated);
      setConfirmOpen(false);
      toast({ title: t('gymAdmin.devices.firmware.sent') });
    } catch (error) {
      toast({
        title: t('gymAdmin.devices.firmware.sendError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  }, [gymSlug, device.id, onDeviceChange, toast, t]);

  // The gym-admin endpoints always carry the block; a device loaded from
  // somewhere else simply has nothing to show.
  if (!firmware) return null;

  const latest = firmware.latest;

  return (
    <>
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-medium">{t('gymAdmin.devices.firmware.title')}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {firmware.version
                  ? t('gymAdmin.devices.firmware.currentVersion', { version: firmware.version })
                  : t('gymAdmin.devices.firmware.versionUnknown')}
              </p>
            </div>
            <Badge variant={STATUS_VARIANT[firmware.status]}>
              {firmware.status === 'up_to_date' && <CheckCircle2 className="mr-1 size-3" />}
              {firmware.status === 'updating' && <Loader2 className="mr-1 size-3 animate-spin" />}
              {firmware.status === 'update_failed' && <AlertTriangle className="mr-1 size-3" />}
              {t(`gymAdmin.devices.firmware.status.${firmware.status}`)}
            </Badge>
          </div>

          {latest && firmware.status !== 'up_to_date' && (
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-sm font-medium">
                {t('gymAdmin.devices.firmware.latestVersion', { version: latest.version })}
              </p>
              {latest.notes && (
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                  {latest.notes}
                </p>
              )}
            </div>
          )}

          {/* Each of these says what to do next, which is different every time:
              wait, retry, or call us because a cable is the only way in. */}
          {firmware.status === 'updating' && (
            <p className="text-sm text-muted-foreground">
              {t('gymAdmin.devices.firmware.updatingHint')}
            </p>
          )}
          {firmware.status === 'update_failed' && (
            <p className="text-sm text-destructive">
              {t('gymAdmin.devices.firmware.failedHint')}
            </p>
          )}
          {firmware.status === 'unknown' && (
            <p className="text-sm text-muted-foreground">
              {t('gymAdmin.devices.firmware.unknownHint')}
            </p>
          )}
          {firmware.status === 'unmanaged' && (
            <p className="text-sm text-muted-foreground">
              {t('gymAdmin.devices.firmware.unmanagedHint')}
            </p>
          )}

          {firmware.can_update && (
            <div className="flex justify-end">
              <Button onClick={() => setConfirmOpen(true)} disabled={sending || updating}>
                {firmware.status === 'update_failed' ? (
                  <RefreshCw className="mr-2 size-4" />
                ) : (
                  <Download className="mr-2 size-4" />
                )}
                {firmware.status === 'update_failed'
                  ? t('gymAdmin.devices.firmware.retry')
                  : t('gymAdmin.devices.firmware.update')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('gymAdmin.devices.firmware.confirm.title', { version: latest?.version ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('gymAdmin.devices.firmware.confirm.checklist')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t('gymAdmin.devices.firmware.confirm.poweredOn')}</li>
            <li>{t('gymAdmin.devices.firmware.confirm.online')}</li>
            <li>{t('gymAdmin.devices.firmware.confirm.duration')}</li>
          </ul>
          <p className="text-sm text-muted-foreground">
            {t('gymAdmin.devices.firmware.confirm.safety')}
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // The dialog closes on its own action click; kept open so the
                // spinner and any error land where the user is still looking.
                event.preventDefault();
                handleConfirm();
              }}
              disabled={sending}
            >
              {sending && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('gymAdmin.devices.firmware.confirm.send')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
